import React, { useCallback, useState } from "react";
import {useQueryPharmacies} from "../../queries/pharmacy/hooks/useQueryPharmacies";
import { PHARMACIES_LIST_ORDER_BY } from "../../queries/pharmacy/pharmacyTypeDefs";
import {Box, CircularProgress, InputAdornment, LinearProgress, TextField, Typography, debounce} from "@mui/material";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PharmacyListItem from "./PharmacyListItem";
import moment from "moment";
import { useQueryActivePharmacyVacations } from "../../queries/pharmacy/hooks/useQueryActivePharmacyVacations";
import { pageTitleSx } from "../../constants/designTokens";

const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const COLUMNS = ['Legal name', 'Email', 'Created', 'Phone', 'Created by', 'Vacation', 'Status'];

const Pharmacies:React.FunctionComponent = () => {
  const [searchInput, setSearchInput] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const {data, loading, refetch} = useQueryPharmacies({
    fetchPolicy: 'no-cache',
    variables: { orderBy: PHARMACIES_LIST_ORDER_BY },
  });
  const { data: vacationData } = useQueryActivePharmacyVacations({
    fetchPolicy: 'no-cache',
  });

  const createData = ( id: number, name: string, user: string, email: string, created: string, phone: string, status: any, vacationDates: string ) => {
    return { id, name, user, email, created, phone, status, vacationDates };
  }

  const getRows = () => {
    const vacationMap = new Map<number, string>();
    (vacationData?.activePharmacyVacations || []).forEach((item: any) => {
      vacationMap.set(
        item.pharmacyId,
        `${moment(item.vacationStartAt).format('MM/DD/YYYY')} - ${moment(item.vacationEndAt).format('MM/DD/YYYY')}`,
      );
    });

    return data.findManyPharmacy.map((pharmacy: any) => {
      const username = `${pharmacy.PharmacyUsers[0]?.User?.firstName} ${pharmacy.PharmacyUsers[0]?.User?.lastName}`
      return createData(
        pharmacy.id,
        pharmacy.legalName,
        username,
        pharmacy.email,
        pharmacy.createdAt,
        pharmacy.phone,
        pharmacy.PharmacyStatus,
        vacationMap.get(pharmacy.id) || '',
      );
    });
  }

  const search = useCallback(debounce(async (value: string) => {
    setQueryLoading(true);

    let andQuery: any = {};

    if (value) {
      andQuery = { legalName: { contains: value, mode: 'insensitive' } }
    }

    await refetch({
      where: andQuery,
      orderBy: PHARMACIES_LIST_ORDER_BY,
    });

    setQueryLoading(false);
  }, 1000), []);

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: '#2563EB' }} />
      </Box>
    );
  }

  const rows = getRows();

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)',
        overflow: 'hidden',
        fontFamily: BODY_FONT,
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Box>
          <Typography component="h1" sx={pageTitleSx}>
            Pharmacies
          </Typography>
          <Box sx={{ fontSize: 13, color: '#475569', fontFamily: BODY_FONT }}>
            {rows.length} {rows.length === 1 ? 'pharmacy' : 'pharmacies'}
          </Box>
        </Box>
        <TextField
          type="text"
          placeholder="Search by legal name…"
          value={searchInput}
          onChange={(event: any) => handleSearch(event.target.value)}
          size='small'
          sx={{
            minWidth: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontFamily: BODY_FONT,
              fontSize: 14,
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#475569' },
              '&.Mui-focused fieldset': { borderColor: '#2563EB', borderWidth: '1.5px' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 18, color: '#475569' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ height: 2 }}>
        {queryLoading && (
          <LinearProgress
            sx={{
              height: 2,
              bgcolor: '#F1F5F9',
              '& .MuiLinearProgress-bar': { bgcolor: '#2563EB' },
            }}
          />
        )}
      </Box>
      <TableContainer>
        <Table sx={{ minWidth: 760 }} aria-label="pharmacies table">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column}
                  align="left"
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '10.5px',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#1E293B',
                    bgcolor: '#F1F5F9',
                    borderBottom: '1px solid #E2E8F0',
                    py: 1.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ border: 0 }}>
                  <Box sx={{ py: 8, textAlign: 'center', fontFamily: BODY_FONT }}>
                    <Box sx={{ fontSize: 15, fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                      No pharmacies found
                    </Box>
                    <Box sx={{ fontSize: 13.5, color: '#475569' }}>
                      Try a different legal name.
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: any) => (
                <React.Fragment key={row.id}>
                  <PharmacyListItem row={row} />
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Pharmacies;
